import {
  StyleSheet,
  View,
  ViewStyle,
  Image,
  ImageStyle,
  Pressable,
  TextStyle,
} from 'react-native';
import React, {useState, useEffect} from 'react';
//ui-kit and styling related imports
import colors from 'theme/Colors';
import {corner_radius, spacing} from 'theme/Units';
import {Text} from 'ui-kits/Text';
//redux imports
import {useSelector, useDispatch} from 'react-redux';
import {
  selectedEmployeeData,
  selectedEmployeeID,
  employeeFailure,
  employeeSuccess,
} from 'redux/selectors/employeeSelector';
// routing related imports
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {
  employeeAssignedProjects,
  resetMessage,
  getEmployeeDetailsRequest,
} from 'redux/slices/employeeSlice';
import {Icon, StatusTag} from 'ui-kits';
import {projectStatus as projectStatusEnum} from 'constants/enums';
import {getFormattedDate} from 'utils/getFormattedDate';
import {defaultUserProfile} from 'constants/DefaultValues';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {useAuthAccess, useToast} from 'hooks';

const Info = ({label, value}: {label: string; value: Date}) => (
  <View style={styles.info}>
    <Text style={styles.label} variant="sourcesans18m">
      {label}
    </Text>
    <Text style={styles.colon} variant="sourcesans18m">
      :
    </Text>
    <Text style={styles.value} variant="sourcesans18m">
      {getFormattedDate(new Date(value))}
    </Text>
  </View>
);

function ProjectCard({project}: Readonly<{project: employeeAssignedProjects}>) {
  const {name, status, end_date} = project;
  return (
    <View style={styles.projectCard}>
      <View style={styles.projHeader}>
        <Text variant="sourcesans18m">{name}</Text>
        <View>
          <StatusTag
            variant={projectStatusEnum[status]}
            text={projectStatusEnum[status]}
          />
        </View>
      </View>
      <Text variant="sourcesans12">
        {getFormattedDate(new Date(end_date as Date), 'yyyy-mm-dd', '/')}
      </Text>
    </View>
  );
}

export function EmployeeDetails({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const employeeID = useSelector(selectedEmployeeID);
  const employeeData = useSelector(selectedEmployeeData);
  const getEmployeeSuccess = useSelector(employeeSuccess);
  const getEmployeeFailure = useSelector(employeeFailure);
  const dispatch = useDispatch();
  const {showToasts} = useToast();
  const {hasWriteAccess} = useAuthAccess();

  useEffect(() => {
    if (employeeID) {
      dispatch(getEmployeeDetailsRequest({id: employeeID}));
    }
  }, [dispatch, employeeID]);

  // error message
  useEffect(() => {
    if (getEmployeeFailure) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(getEmployeeFailure as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, getEmployeeFailure]);

  // success message toast
  useEffect(() => {
    if (getEmployeeSuccess) {
      showToasts(
        SUCCESS_KEY,
        getSuccessMessage(getEmployeeSuccess as SuccessCodes),
        'success',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, showToasts, getEmployeeSuccess]);

  const [startIndex, setStartIndex] = useState(0);
  const project_per_page = 3;

  const handleNext = () => {
    if (startIndex + project_per_page < employeeData?.project?.length!) {
      setStartIndex(startIndex + project_per_page);
    }
  };

  const handlePrev = () => {
    if (startIndex - project_per_page >= 0) {
      setStartIndex(startIndex - project_per_page);
    }
  };

  function handleEdit() {
    navigation.navigate(Routes.UPDATE_EMPLOYEE_DETAILS, {
      id: employeeID?.toString()!,
    });
  }
  function goBack() {
    navigation.navigate(Routes.VIEWEMPLOYEE);
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text variant="header" style={{color: colors.palette.brandBlue}}>
          Employee Details
        </Text>
        <Icon type={'back_arrow'} onPress={goBack} />
      </View>
      <View style={styles.detailsWrapper}>
        <View style={styles.details}>
          <View style={styles.profileSection}>
            <Image
              resizeMode="cover"
              source={{
                uri: employeeData?.profile_photo_url! ?? defaultUserProfile,
              }}
              style={styles.profileImage}
            />
            <View style={styles.basicDetails}>
              <View style={styles.edit}>
                <Text variant="sourcesans16">{employeeData?.emp_id!}</Text>

                {hasWriteAccess ? (
                  <Icon type="edit_blue" onPress={handleEdit} />
                ) : null}
              </View>
              <Text variant="inter24">
                {employeeData?.first_name?.concat(
                  ' ',
                  employeeData?.last_name ?? '',
                )}
              </Text>
              <Text variant="sourcesans16">
                {employeeData?.is_active! ? 'Active' : 'Inactive'}
              </Text>
            </View>
          </View>
          <View style={styles.bottomBorder} />
          <View style={styles.dates}>
            <Info
              label="Date of Joining"
              value={employeeData?.date_of_joining as any}
            />
            <Info label="Date of Birth" value={employeeData?.dob as any} />
            <Info
              label="Last Working Day"
              value={employeeData?.last_day_of_work ? employeeData?.last_day_of_work as any : ''}
            />
          </View>
        </View>

        <View>
          <Text variant="inter24">Assigned Projects</Text>
          <View style={styles.projects}>
            <Pressable
              style={[
                styles.arrow,
                startIndex === 0 && {
                  backgroundColor: colors.palette.stroke,
                },
              ]}
              onPress={handlePrev}>
              <Image
                style={styles.arrowImage}
                source={require('assets/icons/cheveron_up@2x.png')}
              />
            </Pressable>
            <View style={styles.projectList}>
              {employeeData?.project
                ?.slice(startIndex, startIndex + project_per_page)
                .map(proj => (
                  <ProjectCard project={proj} key={proj.id} />
                ))}
            </View>
            <Pressable
              style={[
                styles.arrow,
                startIndex + project_per_page >=
                  employeeData?.project?.length! && {
                  backgroundColor: colors.palette.stroke,
                },
              ]}
              onPress={handleNext}>
              <Image
                style={styles.arrowImage}
                source={require('assets/icons/cheveron_down@2x.png')}
              />
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
}

type styleProps = {
  container: ViewStyle;
  header: ViewStyle;
  projects: ViewStyle;
  dates: ViewStyle;
  info: ViewStyle;
  edit: ViewStyle;
  bottomBorder: ViewStyle;
  basicDetails: ViewStyle;
  profileImage: ImageStyle;
  profileSection: ViewStyle;
  details: ViewStyle;
  detailsWrapper: ViewStyle;
  projectCard: ViewStyle;
  projHeader: ViewStyle;
  projectList: ViewStyle;
  arrow: ViewStyle;
  arrowImage: ImageStyle;
  label: TextStyle;
  colon: TextStyle;
  value: TextStyle;
};
const styles: styleProps = StyleSheet.create({
  arrow: {
    width: 40,
    height: 40,
    borderRadius: 999,
    backgroundColor: colors.palette.brandBlue,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
  },
  arrowImage: {
    width: 16,
    height: 16,
    resizeMode: 'contain',
  },
  projHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  projectCard: {
    backgroundColor: colors.palette.white,
    borderRadius: corner_radius.rounded,
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    paddingVertical: spacing['3'],
    paddingHorizontal: spacing['4'],
  },
  dates: {
    gap: spacing['4'],
  },
  info: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  label: {
    minWidth: 160,
  },
  colon: {
    width: 15,
    textAlign: 'center',
  },
  value: {
    flex: 1,
  },
  edit: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bottomBorder: {
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    alignSelf: 'stretch',
  },
  basicDetails: {
    flexDirection: 'column',
    gap: spacing['1'],
  },
  profileSection: {
    flexDirection: 'row',
    gap: spacing['8'],
    alignItems: 'center',
  },
  profileImage: {
    width: 140,
    height: 140,
    borderRadius: 999,
  },
  container: {
    flex: 1,
    paddingVertical: spacing['6'],
    paddingHorizontal: spacing['8'],
  },
  header: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailsWrapper: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing['8'],
    marginHorizontal: spacing['16'],
    marginVertical: spacing['8'],
  },
  details: {
    flex: 2,
    gap: spacing['6'],
    margin: spacing['2'],
    maxWidth: '60%',
    alignItems: 'flex-start',
  },
  projects: {
    flex: 1,
    backgroundColor: colors.palette.brandBlue10,
    borderRadius: corner_radius['rounded-md'],
    maxHeight: 'fit-content',
    width: 300,
    marginTop: spacing['2'],
    gap: spacing['3'],
    justifyContent: 'center',
  },
  projectList: {
    gap: spacing['1'],
    margin: spacing['1'],
  },
});
